import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';

/**
 * Hook que carga la lista de tipos de artículo desde el backend.
 *
 * Se usa principalmente en el InventarioSidebar para renderizar el
 * catálogo navegable de tipos, y en TiposList para la pantalla de
 * gestión.
 *
 * Devuelve también una función `refresh` que vuelve a tirar el GET,
 * útil tras crear/borrar un tipo desde la UI.
 *
 * Nota: si más adelante introducimos un cache global (React Query o
 * similar), este hook se reemplaza sin cambiar la firma. Por ahora
 * cada montaje del sidebar dispara una llamada (es 1 query/sesión
 * en práctica porque el sidebar no se desmonta).
 */

export function useAssetTypes() {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/tipos');
      // Backend devuelve { data, count }
      setTipos(response?.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // Puente entre useTiposCrud (gestión) y este hook (sidebar global):
  // cualquier mutación dispara 'inventario:tipos-changed' en window y
  // todos los consumidores se refrescan sin recargar la página. El
  // nombre del evento debe coincidir con el de useTiposCrud.
  useEffect(() => {
    function onChanged() {
      fetchTypes();
    }
    window.addEventListener('inventario:tipos-changed', onChanged);
    return () =>
      window.removeEventListener('inventario:tipos-changed', onChanged);
  }, [fetchTypes]);

  return { tipos, loading, error, refresh: fetchTypes };
}
